import React, { useState, useEffect } from 'react';
import { API_URL } from '../services/api';
import {
  Grid,
  Container,
  Typography,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Box,
  Chip,
  Pagination,
  Skeleton
} from '@mui/material';

import { Link } from 'react-router-dom';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';

interface NewsItem {
 id: number;
 title: string;
 slug: string;
 excerpt: string;
 content: string;
 featured_image: string;
 published_at: string;
 views: number;
 author_name: string;
 categories: string;
}

const Noticias: React.FC = () => {
 const [news, setNews] = useState<NewsItem[]>([]);
 const [loading, setLoading] = useState(true);
 const [page, setPage] = useState(1);
 const [totalPages, setTotalPages] = useState(1);
 const itemsPerPage = 6;

 useEffect(() => {
 fetchNews();
 }, [page]);

 const fetchNews = async () => {
 setLoading(true);
 try {
 const offset = (page - 1) * itemsPerPage;
 const response = await axios.get(
 `${API_URL}/api/news?limit=${itemsPerPage}&offset=${offset}`
 );
 setNews(response.data.news);
 setTotalPages(Math.ceil(response.data.total / itemsPerPage));
 } catch (error) {
 console.error('Error fetching news:', error);
 } finally {
 setLoading(false);
 }
 };

 const formatDate = (dateString: string) => {
 const date = new Date(dateString);
 return date.toLocaleDateString('es-ES', {
 day: 'numeric',
 month: 'long',
 year: 'numeric'});
 };

 const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
 setPage(value);
 window.scrollTo({ top: 0, behavior: 'smooth' });
 };

 return (
 <Container maxWidth="lg" sx={{ mt: 4 }}>
 <Typography
 variant="h2"
 sx={{
 textAlign: 'center',
 mb: 2,
 fontWeight: 700,
 letterSpacing: '0.1em'}}
 >
 NOTICIAS
 </Typography>

 <Typography
 variant="h5"
 sx={{
 textAlign: 'center',
 mb: 6,
 opacity: 0.8,
 fontWeight: 300}}
 >
 Las últimas novedades del mundo de la música
 </Typography>

 <Grid container spacing={4}>
 {loading
 ? Array.from(new Array(6)).map((_, index) => (
 <Grid item xs={12} md={6} lg={4} key={index}>
 <Card sx={{ bgcolor: 'background.paper' }}>
 <Skeleton variant="rectangular" height={200} />
 <CardContent>
 <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
 <Skeleton variant="text" />
 <Skeleton variant="text" />
 <Skeleton variant="text" />
 </CardContent>
 </Card>
 </Grid>
 ))
 : news.map((item) => (
 <Grid item xs={12} md={6} lg={4} key={item.id}>
 <Card
 sx={{
 height: '100%',
 display: 'flex',
 flexDirection: 'column',
 background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
 transition: 'transform 0.3s',
 '&:hover': {
 transform: 'scale(1.03)'}}}
 >
 {item.featured_image && (
 <CardMedia
 component="img"
 height="200"
 image={`${API_URL}${item.featured_image}`}
 alt={item.title}
 sx={{ objectFit: 'cover' }}
 />
 )}
 <CardContent sx={{ flex: 1 }}>
 <Box sx={{ mb: 2 }}>
 {item.categories && (
 <Chip
 label={item.categories.split(',')[0]}
 size="small"
 color="primary"
 sx={{ mr: 1 }}
 />
 )}
 </Box>

 <Typography
 variant="h6"
 sx={{
 mb: 1,
 fontWeight: 600,
 overflow: 'hidden',
 textOverflow: 'ellipsis',
 display: '-webkit-box',
 WebkitLineClamp: 2,
 WebkitBoxOrient: 'vertical'}}
 >
 {item.title}
 </Typography>

 <Typography
 variant="body2"
 color="text.secondary"
 sx={{
 mb: 2,
 overflow: 'hidden',
 textOverflow: 'ellipsis',
 display: '-webkit-box',
 WebkitLineClamp: 3,
 WebkitBoxOrient: 'vertical'}}
 >
 {item.excerpt || item.content.substring(0, 150) + '...'}
 </Typography>

 <Box
 sx={{
 display: 'flex',
 alignItems: 'center',
 gap: 2,
 color: 'text.secondary',
 fontSize: '0.875rem'}}
 >
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
 <CalendarTodayIcon fontSize="small" />
 {formatDate(item.published_at)}
 </Box>
 <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
 <VisibilityIcon fontSize="small" />
 {item.views}
 </Box>
 </Box>
 </CardContent>

 <CardActions>
 <Button
 size="small"
 color="primary"
 component={Link}
 to={`/noticias/${item.slug}`}
 fullWidth
 >
 Leer más
 </Button>
 </CardActions>
 </Card>
 </Grid>
 ))}
 </Grid>

 {!loading && news.length === 0 && (
 <Box sx={{ textAlign: 'center', py: 8 }}>
 <Typography variant="h5" color="text.secondary">
 No hay noticias disponibles en este momento
 </Typography>
 </Box>
 )}

 {totalPages > 1 && (
 <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
 <Pagination
 count={totalPages}
 page={page}
 onChange={handlePageChange}
 color="primary"
 size="large"
 />
 </Box>
 )}
 </Container>
 );
};

export default Noticias;